import { apiOrigin, httpJson, raf, withTimeout } from '@/lib/compat'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ŞƏKİL NÖVBƏSİ — donmanın ikinci böyük səbəbi
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * PROBLEM. Ana ekranda 13 sıra × 20 kart = 260 poster var. `<img src>`-ləri
 * eyni anda versək, TV 260 JPEG-i paralel yükləyib DEKODLAYIR. Dekodlama
 * masaüstündən fərqli olaraq TV-də ƏSAS AXINDA gedir (bir çox modeldə
 * `decode()` yoxdur) — hər poster 20-60 ms. Nəticə: bir neçə saniyəlik tam
 * cavabsızlıq. İstifadəçi «TV dondu» deyir.
 *
 * HƏLL.
 *   1. Eyni anda ən çox `MAX_PARALLEL` şəkil yüklənir (TV-də 3 optimaldır:
 *      şəbəkə boş qalmır, dekoder növbəsi isə əsas axını tıxamır).
 *   2. Yalnız GÖRÜNƏN sıraların şəkilləri növbəyə düşür — sıraya fokus
 *      düşəndə `requestRow()` çağırılır (bax focus.ts `onEnterRow`).
 *   3. Ekran dəyişəndə `bumpGeneration()` növbəni ləğv edir — köhnə ekranın
 *      posterləri yenisinin şəkillərinin qabağını kəsmir.
 *   4. Şəkil `Image()` obyektində yüklənir, hazır olanda `src` təyin edilir.
 *      Beləliklə yarımçıq/pilləli boyanma olmur (TV-də hər aralıq kadr paint-dir).
 * ═══════════════════════════════════════════════════════════════════════════
 */

const MAX_PARALLEL = 3

interface Job {
  target: HTMLImageElement
  url: string
  generation: number
}

const queue: Job[] = []
let running = 0
let generation = 0
let pumping = false

/** Ekran dəyişəndə köhnə növbəni ləğv edir. */
export function bumpGeneration(): void {
  generation += 1
  queue.length = 0
}

/**
 * Şəkli NÖVBƏYƏ salır və kadr sərhədində yükləməyə başlayır.
 * ⚠ Birbaşa `img.src` yazılmır: fokus hərəkəti ilə eyni kadrda yükləmə
 * başlamaq həmin kadrı uzadır və hərəkət «tutulmuş» görünür. Paralel yükləmə
 * sayı da məhduddur — köhnə TV-nin şəbəkə yığını çox sorğuda ilişir.
 */
export function queueImage(target: HTMLImageElement, url: string): void {
  if (!url) return
  /**
   * ⚠ SON SİFARİŞ QALİB GƏLİR (2026-08-05, film modalı ilə birlikdə əlavə edildi).
   *   Modal EYNİ `<img>` elementlərini təkrar işlədir (DOM bir dəfə qurulur —
   *   PERFORMANCE.md; oxşar karta OK modalın məzmununu dəyişir — M6), yəni bir
   *   elementə ard-arda İKİ fərqli poster sifariş oluna bilir. Növbədə ləğv
   *   mexanizmi yox idi: köhnə sifariş gec qayıdıb YENİ filmin kartına KÖHNƏ
   *   posteri yazırdı. Atribut son istənən ünvanı saxlayır, `done()` isə yalnız
   *   ona uyğun gələni tətbiq edir.
   * ⚠ QALAN ÇAĞIRANLARDA DAVRANIŞ DƏYİŞMİR: onlar hər elementi bir dəfə sifariş
   *   edir (`ui/card.ts` → `requested` bayrağı), yəni atribut həmişə uyğun gəlir.
   */
  target.setAttribute('data-src', url)
  queue.push({ target, url, generation })
  pump()
}

function pump(): void {
  if (pumping) return
  pumping = true
  // Növbəni kadr sərhədində açırıq: fokus hərəkəti ilə eyni kadrda şəkil
  // yükləməyə başlamaq həmin kadrı uzadır və hərəkət «tutulmuş» görünür.
  raf(() => {
    pumping = false
    while (running < MAX_PARALLEL && queue.length > 0) {
      const job = queue.shift()
      if (!job) break
      if (job.generation !== generation) continue
      start(job)
    }
  })
}

/**
 * Bir şəklin yükləmə həddi.
 *
 * ⚠ NİYƏ LAZIMDIR (2026-08-04 auditi): növbədə eyni anda cəmi `MAX_PARALLEL` = 3
 *   yer var və `<img>` yükləməsinin ÖZ vaxt həddi YOXDUR. Cavab verməyən üç sorğu
 *   (TV-nin şəbəkəsi ilişəndə adi haldır) növbəni ƏBƏDİ bağlayır — ekranda HEÇ BİR
 *   poster görünmür. Sahib bildirişi məhz belə səslənir: «heç bir poster göstərmir».
 *   Layihənin JSON sorğularında hədd onsuz da var (`compat.ts → withTimeout`),
 *   şəkillərdə yox idi.
 * ⚠ 10 saniyə: TV-də zəif Wi-Fi ilə böyük poster 3-5 saniyə çəkə bilər, 10-dan sonra
 *   isə onsuz da istifadəçi sıranı ötüb keçib.
 */
const IMAGE_TIMEOUT_MS = 10000

function start(job: Job): void {
  running += 1
  const loader = new Image()
  /**
   * ⚠ İKİQAT SAYMA MÜHAFİZƏSİ: `done` üç yerdən çağırıla bilər (onload · onerror ·
   *   vaxt həddi). Bayraqsız yazılsaydı vaxt həddi işə düşən sorğu sonradan
   *   `onerror` da atır və `running` İKİ dəfə azalırdı — sayğac mənfiyə düşür,
   *   paralel limit sıradan çıxır və növbə bütün 260 posteri birdən buraxır
   *   (donmanın ta özü — PERFORMANCE.md §4).
   */
  let settled = false
  let timer = 0

  const done = (ok: boolean) => {
    if (settled) return
    settled = true
    if (timer) {
      window.clearTimeout(timer)
      timer = 0
    }
    running -= 1
    // Son sifariş qalib gəlir (bax `queueImage`) — köhnə sifariş sükutla atılır.
    if (ok && job.generation === generation && job.target.getAttribute('data-src') === job.url) {
      job.target.src = job.url
      // `is-loaded` olmayan `<img>` şəffafdır — altındakı başlıqlı fallback
      // görünür. Beləliklə yüklənmə zamanı boş boz qutu olmur.
      job.target.className = job.target.className + ' is-loaded'
    }
    pump()
  }

  loader.onload = () => done(true)
  loader.onerror = () => done(false)
  timer = window.setTimeout(() => {
    // Boş `src` köhnə mühərriklərdə gedən yükləməni dayandırır — yer boşalsın.
    loader.onload = null
    loader.onerror = null
    try {
      loader.src = ''
    } catch {
      // Bəzi köhnə mühərrikdə boş `src` atır — yerin boşalması onsuz da aşağıdadır.
    }
    done(false)
  }, IMAGE_TIMEOUT_MS)
  loader.src = job.url
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MEDİA ORIGIN + ÖLÇÜ VARİANTI — `GET /api/media/config`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠ ORIGIN KODDA SABİT YAZILMIR (2026-08-01). Əvvəl burada CDN sabiti vardı və
 * eyni ünvan BEŞ səthdə üç fərqli dəyərlə təkrarlanırdı, server isə dördüncü
 * vəziyyətdə idi → yeni şəkillər 404. Ünvanı yalnız server bilir — ona görə
 * origin `GET /api/media/config`-dən gəlir. 2026-09-30-dan CDN yoxdur, server
 * həmişə öz origin-ini qaytarır.
 *
 * FALLBACK (config sorğusu alınmasa): `VITE_KB_API_ORIGIN` → boş (dev, Vite proxy
 * `/upload`-u özü həll edir). Paketlənmiş app `file://`-dən açıldığı üçün nisbi
 * yol orada işləmir; API origin-i serverin öz fayllarını verir.
 *
 * ÖLÇÜ VARİANTI. Server `/upload…` və `/uploads…` şəkillərinə `?w=<en>`
 * qəbul edir və WebP qaytarır. TV üçün ƏN BÖYÜK QAZANC BURADADIR: poster
 * dekodlaması bir çox TV modelində ƏSAS AXINDADIR və vaxt PİKSEL SAYI ilə
 * mütənasibdir — yəni PERFORMANCE.md §4-dəki donmanın ta özü.
 * ÖLÇÜLDÜ (20 real poster, CPU ×20): orijinal 2 841 672 bayt / 42.3 MP / dekod
 * 686 ms → `?w=320` **354 166 bayt / 2.8 MP / dekod 357 ms** (8.0× bayt, 15× piksel).
 * ⚠ Yalnız NİSBİ yollara əlavə olunur: mütləq URL (Mux/Vimeo kadrı) imzalı ola
 * bilər və query dəyişsə imza pozulur.
 */
export interface MediaConfig {
  origin: string
  storage: string
  widthParam: string
  qualityParam: string
  widths: number[]
  /**
   * ÖLÇÜLƏNDİRİLMİŞ şəkli verən ünvan — `origin`-dən AYRI ola bilər (2026-08 –
   * 2026-09 arasında orijinal CDN-dən, variant API-dən gedirdi; indi ikisi eynidir).
   */
  variantOrigin: string
}

/** Config gəlməyənə qədər işlənən enlər — serverdəki `ImageVariantMiddleware` ağ siyahısı. */
const DEFAULT_WIDTHS = [96, 160, 216, 320, 432, 640, 960, 1280]

/**
 * TV-də istənilən enlər. Dəyərlər `tv.css`-dəki ELEMENT ÖLÇÜSÜNDƏN çıxır və
 * serverin ağ siyahısındakı NÖVBƏTİ BÖYÜK ənə yuvarlanır (böyütmə olmasın deyə):
 *   · kart posteri  `.card__img` 220×330  → 320
 *   · hero/detal fonu `.hero__bg` 1824×594, `.detail__bg` 1824×700 → 1280 (siyahının maksimumu)
 *   · profil avatarı `.who__avatar` 240×240 → 320
 * ⚠ Kanvas 1920×1080-dir və `#viewport` yalnız MİQYASLANIR (masaüstü nümayişində
 *   kiçilir, TV-də scale=1) — yəni element ölçüsü TV-də faktiki pikseldir.
 */
export const IMAGE_WIDTH = {
  card: 320,
  backdrop: 1280,
  avatar: 320,
}

let origin = apiOrigin()
/**
 * Ölçüləndirilmiş şəkillərin ünvanı. Başlanğıcda API origin-idir — TV app-ı `file://`
 * -dən açıldığı üçün nisbi yol işləmir və ölçüsüz poster TV-ni dondurur.
 */
let variantOrigin = apiOrigin()
let widthParam = 'w'
let widths = DEFAULT_WIDTHS

/**
 * Media konfiqurasiyasını tətbiq edir. `null` = başlanğıc (build) vəziyyətinə qaytarır.
 * Ayrıca ixrac olunur ki, testlər sorğusuz yoxlaya bilsin.
 */
export function applyMediaConfig(config: MediaConfig | null): void {
  if (!config) {
    origin = apiOrigin()
    variantOrigin = apiOrigin()
    widthParam = 'w'
    widths = DEFAULT_WIDTHS
    return
  }
  const next = withPageScheme(trimSlash(config.origin || ''))
  if (next) origin = next
  // Server bu sahəni verməsə (köhnə nüsxə) API origin-i qalır — `?w=` onsuz da
  // yalnız orada işləyir.
  const variant = withPageScheme(trimSlash(config.variantOrigin || ''))
  variantOrigin = variant || apiOrigin()
  if (config.widthParam) widthParam = config.widthParam
  if (config.widths && config.widths.length > 0) widths = config.widths
}

/**
 * Serverin verdiyi HTTP ünvanı səhifənin öz sxeminə qaldırır.
 *
 * ⚠ ÖLÇÜLMÜŞ DEFEKT (2026-08-04, sahib bildirişi «TCL tv-də heç bir poster
 *   göstərmir»): `GET https://api.kinobox.az/api/media/config` cavabında
 *   `variantOrigin` **`http://api.kinobox.az`** gəlirdi. Yəni HTTPS səhifə
 *   bütün posterləri (kart · hero · detal fonu · avatar) HTTP-dən istəyirdi —
 *   ən yaxşı halda hər poster üçün əlavə bir 301 gedişi, ən pis halda TV
 *   brauzerinin qarışıq məzmun bloku (şəkil ÜMUMİYYƏTLƏ gəlmir).
 * ⚠ Kök səbəb serverdədir (`MediaConfigEndpoints.PublicScheme` — Cloudflare +
 *   nginx başlıq sayı uyğunsuzluğu) və ORADA da düzəldildi. Bu funksiya ikinci
 *   müdafiə xəttidir: köhnə API ilə işləyən TV də posterləri görməlidir.
 * ⚠ SAYT, iOS VƏ ANDROID-DƏ BU MÜHAFİZƏ ARTIQ VAR (`web/media.ts:137`,
 *   `MediaConfig.swift`, `MediaConfig.kt`) — dörd client-dən yalnız TV-də yox idi.
 * ⚠ YALNIZ EYNİ HOST üçün: fərqli hostu https-ə qaldırmaq orada sertifikat
 *   yoxdursa şəkli TAMAMİLƏ sındırardı — ona görə host müqayisə olunur.
 * ⚠ Paketlənmiş app `file://`-dən açılır: orada `location.protocol` `https:` deyil,
 *   funksiya heç nə etmir (qarışıq məzmun qaydası da tətbiq olunmur).
 */
function withPageScheme(url: string): string {
  if (!url || url.indexOf('http://') !== 0) return url
  const location = window.location
  if (!location || location.protocol !== 'https:') return url
  // `http://host[:port]/...` → host hissəsi
  const rest = url.slice(7)
  const slash = rest.indexOf('/')
  const host = slash < 0 ? rest : rest.slice(0, slash)
  if (host !== location.host) return url
  return 'https://' + rest
}

/** `media/config` TV-nin ilk kadrını gözlətməsin deyə — kataloq sorğusu ilə eyni hədd. */
const CONFIG_TIMEOUT_MS = 6000

/**
 * Boot-da BİR DƏFƏ çağırılır (kataloq sorğusu ilə PARALEL — bax `main.ts`).
 *
 * ⚠ HEÇ VAXT REJECT ETMİR: media origin-i alınmasa da app açılmalıdır (qara ekran
 * qadağandır — CLAUDE.md §6). Alınmasa build fallback-ı qalır, yəni davranış
 * bu dəyişiklikdən ƏVVƏLKİ ilə eynidir.
 * ⚠ Ekran DOM-u bir dəfə qurulduğu üçün (sıfır reaktivlik) config MÜTLƏQ ilk
 * kartdan ƏVVƏL gəlməlidir — sonradan gəlsə şəkil ünvanları yenilənməz.
 */
export function loadMediaConfig(): Promise<void> {
  return withTimeout(httpJson<MediaConfig>('/api/media/config'), CONFIG_TIMEOUT_MS, 'media/config')
    .then((config) => {
      applyMediaConfig(config)
    })
    .catch(() => {
      // Fallback origin qalır — şəkilsiz ekran olmasın.
    })
}

/**
 * Poster ünvanını mütləq hala gətirir və (en verilibsə) ölçü variantı istəyir.
 *
 * ⚠ Paketlənmiş TV app-ı `file://`-dən açılır — «/upload/…» nisbi yol orada
 * TV-nin fayl sistemində axtarılır və 404 verir. Ona görə mütləq ünvan lazımdır.
 */
export function mediaUrl(path: string | null | undefined, width?: number): string {
  if (!path) return ''
  // Mütləq URL serverin verdiyi hazır ünvandır (CDN/Mux/Vimeo) — origin də,
  // ölçü parametri də ƏLAVƏ EDİLMİR (imzalı URL-də query imzanı pozur).
  if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) return path

  // ⚠ HAZIR PROFİL AVATARLARI (`/avatars/preset/…`) API-nin PUBLIC ünvanından
  //   (`variantOrigin`) gəlir — media origin-i fərqli olsa belə.
  if (path.indexOf('/avatars/') === 0) {
    const api = variantOrigin || origin
    return api ? api + path : path
  }

  // Ölçüsüz (və ya artıq query-li — server tərəfdə imzalı ola bilər) sorğu adi
  // origin-ə gedir; ölçü variantını isə YALNIZ API verir (bax `variantOrigin`).
  if (!width || path.indexOf('?') >= 0) return origin ? origin + path : path

  const base = variantOrigin || origin
  return (base ? base + path : path) + '?' + widthParam + '=' + pickWidth(width)
}

/**
 * İstənilən eni serverin ağ siyahısındakı NÖVBƏTİ BÖYÜK ənə yuvarlayır.
 *
 * Yuvarlama server tərəfdə də var, amma client-də etmək İKİ şeyə lazımdır:
 * keş açarı bütün cihazlarda eyni olsun (fragmentasiya yoxdur) və TV heç vaxt
 * ağ siyahıdan kənar dəyər göndərməsin.
 * ⚠ `Array.prototype.find` Chrome 45+-dır (webOS 3 = Chrome 38) — sadə döngü.
 */
function pickWidth(desired: number): number {
  let best = 0
  let max = 0
  for (let i = 0; i < widths.length; i++) {
    const value = widths[i] as number
    if (value > max) max = value
    if (value >= desired && (best === 0 || value < best)) best = value
  }
  // Siyahıdan böyük en istənibsə maksimumda dayanırıq — server onsuz da böyütmür.
  return best || max || desired
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '')
}
